import { useMemo, useState } from 'react';
import Ajv2020 from 'ajv/dist/2020';
import addFormats from 'ajv-formats';
import type { ErrorObject } from 'ajv';
import { resolveFields } from './schemaWalk';
import { getValueAtPath, setValueAtPath } from './values';
import type { FieldDescriptor, FormEnvelope } from './types';

export interface FormFieldError {
  path: string;
  message: string;
}

export interface UseFormRendererResult {
  /** The ordered, top-level fields to render — see `schemaWalk.ts`. */
  fields: FieldDescriptor[];
  values: Record<string, unknown>;
  errors: FormFieldError[];
  setValue: (path: string, value: unknown) => void;
  /** Appends an empty row to a repeating group at `path`. */
  addRow: (path: string) => void;
  /** Removes row `index` of the repeating group at `path`. */
  removeRow: (path: string, index: number) => void;
  /**
   * Runs client-side validation against the SAME JSON Schema the server
   * evaluates (the AC's "client validation mirrors the server rules"), updates
   * `errors`, and returns whether the current values are valid. Not run on
   * every keystroke — a caller triggers it (typically on submit, or on blur
   * for a single field) rather than this hook deciding that for every case.
   */
  validate: () => boolean;
}

/**
 * The framework-agnostic core of the forms renderer (M1-18) — schema
 * resolution, value state, and validation, with no DOM or React-DOM
 * dependency beyond `useState`/`useMemo` themselves. `FormRenderer.tsx` and
 * `fields/*.tsx` are the web-specific half; a React Native port (M3-12) reuses
 * this hook and swaps only those.
 */
export function useFormRenderer(
  envelope: FormEnvelope,
  initialValues: Record<string, unknown> = {},
): UseFormRendererResult {
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const [errors, setErrors] = useState<FormFieldError[]>([]);

  // Both memos key off the schema/uiSchema themselves, not the whole envelope
  // object — a template, once fetched, never changes during the form's
  // lifetime, so re-resolving fields or recompiling the ajv validator on every
  // unrelated render would be wasted work.
  const fields = useMemo(
    () => resolveFields(envelope.schema, envelope.uiSchema),
    [envelope.schema, envelope.uiSchema],
  );

  const validateFn = useMemo(() => {
    const ajv = new Ajv2020({ allErrors: true });
    addFormats(ajv);
    return ajv.compile(envelope.schema);
  }, [envelope.schema]);

  function setValue(path: string, value: unknown) {
    setValues((previous) => setValueAtPath(previous, path, value));
  }

  function addRow(path: string) {
    // Reads `previous`, not the outer `values` — two calls fired before
    // React re-renders between them (a fast double-click; two clicks
    // dispatched in the same task) would otherwise both compute the new row
    // array from the SAME stale snapshot, and the second call's write would
    // silently overwrite the first's instead of adding a second row.
    setValues((previous) => {
      const current = getValueAtPath(previous, path);
      const rows = Array.isArray(current) ? current : [];
      return setValueAtPath(previous, path, [...rows, {}]);
    });
  }

  function removeRow(path: string, index: number) {
    setValues((previous) => {
      const current = getValueAtPath(previous, path);
      const rows = Array.isArray(current) ? current : [];
      return setValueAtPath(previous, path, rows.filter((_, i) => i !== index));
    });
  }

  function validate(): boolean {
    const valid = validateFn(values);
    setErrors(valid ? [] : toFieldErrors(validateFn.errors ?? []));
    return valid;
  }

  return { fields, values, errors, setValue, addRow, removeRow, validate };
}

/**
 * ajv reports an applicator keyword's own failure ALONGSIDE the leaf failure
 * inside it: a schema with `if`/`then` that is missing a conditionally-required
 * field yields both `required` (naming the field) and `if` (saying only that
 * the "then" branch did not match). The second is a restatement with no field
 * and no wording a technician can act on — and, worse, it inflates the count,
 * so one empty box reads as "2 fields need attention".
 *
 * Surfaced when M1-21's starter templates became the first stored templates to
 * use `if`/`then` at all. They are dropped, unless they are ALL there is: a form
 * that refuses to submit while reporting no problems would be worse than a
 * clumsy message.
 */
const ApplicatorKeywords = new Set(['if', 'anyOf', 'oneOf', 'allOf', 'not', 'then', 'else']);

function toFieldErrors(errors: ErrorObject[]): FormFieldError[] {
  const specific = errors.filter((error) => !ApplicatorKeywords.has(error.keyword));
  return (specific.length > 0 ? specific : errors).map(toFieldError);
}

function toFieldError(error: ErrorObject): FormFieldError {
  const basePath = error.instancePath.replace(/^\//, '').replace(/\//g, '.');

  // A missing-required-property error's instancePath points at the PARENT
  // object, not the field that's actually missing — without this, every
  // missing field on a form would report as one error on the root, which
  // tells a technician nothing about which of five required fields to fill in.
  if (error.keyword === 'required' && typeof error.params.missingProperty === 'string') {
    const path = basePath ? `${basePath}.${error.params.missingProperty}` : error.params.missingProperty;
    return { path, message: `${error.params.missingProperty} is required.` };
  }

  return { path: basePath, message: error.message ?? 'This value is not valid.' };
}
